import { type CustomerSummary, customerKindLabel, partyIdentificationLabel } from '@facture/contracts'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  UsersIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { ImportExportMenu } from '@/components/forms/import-export-menu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ClearFilters, FilterBar, FilterSearch, FilterSelect, FilterTabs } from '@/components/ui/filter-bar'
import { Avatar } from '@/components/ui/misc'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { customerQuery, customersQuery, useSetCustomerActive } from '@/features/customers/api'
import { CustomerDetailSheet } from '@/features/customers/customer-detail-sheet'
import { CustomerFormSheet } from '@/features/customers/customer-form-sheet'
import { CustomerImportDialog } from '@/features/customers/customer-import-dialog'
import { formatIdentification, formatMoney } from '@/lib/format'
import { useCan } from '@/lib/session'

const searchSchema = z.object({
  q: z.string().optional(),
  tab: z.enum(['all', 'customer', 'supplier']).optional(),
  status: z.enum(['active', 'archived']).optional(),
  page: z.number().int().min(1).optional(),
  id: z.string().optional(),
})

export const Route = createFileRoute('/_app/customers')({
  staticData: { crumb: 'Clientes' },
  validateSearch: searchSchema,
  component: CustomersPage,
})

const col = columnHelper<CustomerSummary>()

function CustomersPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCan('customers:write')
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const [formOpen, setFormOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const setActive = useSetCustomerActive()

  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const query = {
    search: search.q,
    kind: search.tab && search.tab !== 'all' ? search.tab : undefined,
    status: search.status ?? 'active',
    page: search.page ?? 1,
    pageSize: 25,
  } as const
  const list = useQuery(customersQuery(query))
  // La exportación respeta la búsqueda y los filtros vigentes de la lista.
  const exportParams = new URLSearchParams(
    Object.entries({ search: query.search, kind: query.kind, status: query.status }).flatMap(([k, v]) =>
      v ? [[k, v]] : [],
    ),
  ).toString()
  const editing = useQuery({ ...customerQuery(editingId ?? ''), enabled: !!editingId })

  // El texto buscado vive en la URL (compartible); se sincroniza al dejar de teclear.
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const columns = useMemo(
    () => [
      col.accessor('name', {
        header: 'Nombre',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar name={row.original.name} />
            <div className="min-w-0">
              <div className="truncate font-medium">{row.original.name}</div>
              <div className="truncate text-muted-foreground text-xs">
                {row.original.tradeName ?? row.original.email ?? '—'}
              </div>
            </div>
          </div>
        ),
      }),
      col.accessor('identification', {
        header: 'Identificación',
        meta: { hideOnMobile: true },
        cell: ({ row }) =>
          row.original.identification ? (
            <div>
              <div className="font-mono text-[13px]">
                {formatIdentification(row.original.identificationType ?? '', row.original.identification)}
              </div>
              <div className="text-muted-foreground text-xs">
                {row.original.identificationType
                  ? partyIdentificationLabel[row.original.identificationType]
                  : ''}
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      }),
      col.accessor('kind', {
        header: 'Tipo',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <Badge variant={getValue() === 'supplier' ? 'info' : getValue() === 'both' ? 'primary' : 'neutral'}>
            {customerKindLabel[getValue()]}
          </Badge>
        ),
      }),
      col.accessor('phone', {
        header: 'Teléfono',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => <span className="text-muted-foreground tabular">{getValue() ?? '—'}</span>,
      }),
      col.accessor('balance', {
        header: 'Saldo',
        meta: { align: 'right' },
        cell: ({ getValue }) => (
          <span className={getValue() > 0 ? 'font-medium' : 'text-muted-foreground'}>
            {formatMoney(getValue())}
          </span>
        ),
      }),
      col.display({
        id: 'actions',
        header: '',
        meta: { align: 'right', className: 'w-12' },
        cell: ({ row }) =>
          canWrite ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon-sm" aria-label="Acciones">
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  onSelect={() => {
                    setEditingId(row.original.id)
                    setFormOpen(true)
                  }}
                >
                  <PencilIcon /> Editar
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    setActive.mutate(
                      { id: row.original.id, isActive: !row.original.isActive },
                      {
                        onSuccess: () =>
                          toast.success(row.original.isActive ? 'Contacto archivado' : 'Contacto restaurado'),
                      },
                    )
                  }
                >
                  {row.original.isActive ? <ArchiveIcon /> : <ArchiveRestoreIcon />}
                  {row.original.isActive ? 'Archivar' : 'Restaurar'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null,
      }),
    ],
    [canWrite, setActive.mutate],
  )

  return (
    <Page>
      <PageHeader
        title="Clientes y proveedores"
        description="Receptores de tus comprobantes y emisores de tus compras."
        actions={
          <>
            <ImportExportMenu
              exportPath={`/customers/export.xlsx?${exportParams}`}
              exportFilename="clientes.xlsx"
              templatePath="/customers/import/template.xlsx"
              templateFilename="plantilla-clientes.xlsx"
              onImport={canWrite ? () => setImportOpen(true) : undefined}
            />
            {canWrite && (
              <Button
                onClick={() => {
                  setEditingId(null)
                  setFormOpen(true)
                }}
              >
                <PlusIcon /> Nuevo contacto
              </Button>
            )}
          </>
        }
      />

      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(c) => c.id}
        onRowClick={(c) => navigate({ to: '/customers/$id', params: { id: c.id } })}
        isRowMuted={(c) => !c.isActive}
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Tipo de contacto"
              value={search.tab ?? 'all'}
              onValueChange={(v) => setSearch({ tab: v as 'all', page: undefined })}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'customer', label: 'Clientes' },
                { value: 'supplier', label: 'Proveedores' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por nombre o cédula…" />
            <FilterSelect
              label="Activos o archivados"
              width="sm"
              value={search.status ?? 'active'}
              onValueChange={(v) =>
                setSearch({ status: v === 'active' ? undefined : (v as 'archived'), page: undefined })
              }
              options={[
                { value: 'active', label: 'Activos' },
                { value: 'archived', label: 'Archivados' },
              ]}
            />
            <ClearFilters
              active={!!(search.q || (search.tab && search.tab !== 'all') || search.status)}
              onClear={() => {
                setText('')
                setSearch({ q: undefined, tab: undefined, status: undefined, page: undefined })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={UsersIcon}
            title={q ? 'Sin coincidencias' : 'Todavía no hay contactos'}
            description={
              q ? `Nada coincide con “${q}”.` : 'Agregá tu primer cliente para empezar a emitir comprobantes.'
            }
            action={
              !q &&
              canWrite && (
                <Button onClick={() => setFormOpen(true)}>
                  <PlusIcon /> Nuevo contacto
                </Button>
              )
            }
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />

      <CustomerDetailSheet
        id={search.id}
        onClose={() => setSearch({ id: undefined })}
        onEdit={() => {
          setEditingId(search.id ?? null)
          setFormOpen(true)
        }}
      />
      <CustomerImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <CustomerFormSheet
        open={formOpen && (!editingId || !!editing.data)}
        onOpenChange={(open) => {
          setFormOpen(open)
          if (!open) setEditingId(null)
        }}
        customer={editingId ? editing.data : null}
        defaultKind={search.tab === 'supplier' ? 'supplier' : 'customer'}
      />
    </Page>
  )
}
